import { z } from "zod";

import type { NavigationCategory } from "~/components/hec-header/mobile-nav/types";
import type { ICategoryEnrichmentEntry } from "~/hec/types/plp";

/**
 * One top-level chip: a CIO group / nav category id, an optional label
 * override, and the Builder image it draws. No image: text only, as a PLP
 * category pill with no enrichment icon.
 */
export interface Chip {
  id: string;
  label?: string;
  iconUrl?: string;
}

/** "All" (CIO's whole-catalog `root`), then the legacy Fastr page's 7 in its order: the list when none is authored. */
const ALL_CHIP: Chip = { id: "root" };

export const DEFAULT_CHIPS: readonly Chip[] = [
  ALL_CHIP,
  { id: "best-sellers" },
  { id: "all-bundles" },
  { id: "personal-care" },
  { id: "medicine-and-treatments" },
  { id: "joint-and-muscle-pain-relief" },
  { id: "baby-and-mom" },
  { id: "home-health-care" },
];

/** Ids with an i18n label, so they show without a nav node (a nav brown-out). */
export const LABELLED_CHIP_IDS = [
  "root",
  "best-sellers",
  "all-bundles",
] as const;
export type LabelledChipId = (typeof LABELLED_CHIP_IDS)[number];
const LABELLED: readonly string[] = LABELLED_CHIP_IDS;

/**
 * A Builder image URL: `type: "file"` inputs and `categoryIcon` both arrive as
 * absolute https strings. `.url()` alone also accepts `javascript:` and
 * `data:`, hence the scheme check. Anything else is no icon, never an error.
 */
const httpsUrl = z
  .string()
  .trim()
  .url()
  .refine(url => /^https:\/\//i.test(url))
  .optional()
  .catch(undefined);

const authoredChip = z.object({
  categoryId: z.string().trim().min(1),
  label: z.string().trim().min(1).optional().catch(undefined),
  image: httpsUrl,
});

/**
 * `data.categoryShortName` → `data.categoryIcon` from the PLP's Builder
 * `category-enrichment` entries: the same icons the PLP's category pills draw.
 * The last entry for an id wins, as `mergeWithBuilderEnrichment` does. An
 * entry's `excludeFromCategoryIconsBanner` hides its category from the PLP's
 * icon banner; which chips the spender shows is authored separately, so it is
 * not read here.
 */
function enrichmentIcons(
  enrichment: readonly ICategoryEnrichmentEntry[],
): Map<string, string> {
  const icons = new Map<string, string>();
  for (const entry of enrichment) {
    const id = entry?.data?.categoryShortName;
    const url = httpsUrl.parse(entry?.data?.categoryIcon);
    if (typeof id === "string" && id.trim() && url) icons.set(id.trim(), url);
  }
  return icons;
}

const shown = (
  chips: readonly Chip[],
  categories: readonly NavigationCategory[],
) =>
  chips.filter(
    chip =>
      LABELLED.includes(chip.id) || categories.some(n => n.id === chip.id),
  );

/**
 * The chips the row draws, from the SpenderChipRow block's authored `chips`
 * (bad entries dropped, first of a duplicate id kept). A chip shows only when
 * the meganav filter keeps its node (or it has an i18n label), so an authored
 * label never bypasses LMN/menu gating. Nothing authored or nothing shown:
 * the defaults. Each chip's icon is its authored upload, else its category's
 * enrichment icon, else none.
 */
export function resolveChips(
  raw: unknown,
  categories: readonly NavigationCategory[],
  enrichment: readonly ICategoryEnrichmentEntry[] = [],
): Chip[] {
  const icons = enrichmentIcons(enrichment);
  const withIcon = (chip: Chip, authoredImage?: string): Chip => ({
    ...chip,
    iconUrl: authoredImage ?? icons.get(chip.id),
  });
  const authored = z
    .array(z.unknown())
    .catch([])
    .parse(raw)
    .flatMap(entry => {
      const parsed = authoredChip.safeParse(entry);
      return parsed.success
        ? [
            withIcon(
              { id: parsed.data.categoryId, label: parsed.data.label },
              parsed.data.image,
            ),
          ]
        : [];
    })
    .filter((chip, i, all) => all.findIndex(c => c.id === chip.id) === i);
  const visible = shown(authored, categories);
  if (visible.length === 0) {
    return shown(DEFAULT_CHIPS, categories).map(chip => withIcon(chip));
  }
  // "All" always leads, authored or not.
  return visible.some(chip => chip.id === ALL_CHIP.id)
    ? visible
    : [withIcon(ALL_CHIP), ...visible];
}

/** The chips' nav nodes (with their children), the only part of the tree the row reads. */
export const chipNodes = (
  categories: readonly NavigationCategory[],
  chips: readonly Chip[],
) => categories.filter(c => chips.some(chip => chip.id === c.id));

/** The first chip: what `?cat=` falls back to, and the one the URL omits. */
export const defaultChipId = (chips: readonly Chip[]) => chips[0]?.id ?? "";

export function allowedChipIds(
  categories: readonly NavigationCategory[],
  chips: readonly Chip[],
): Set<string> {
  const ids = new Set(chips.map(chip => chip.id));
  for (const node of chipNodes(categories, chips)) {
    for (const child of node.categories ?? []) ids.add(child.id);
  }
  return ids;
}

export function chipFamily(
  id: string,
  categories: readonly NavigationCategory[],
  chips: readonly Chip[],
): string | null {
  if (chips.some(chip => chip.id === id)) return id;
  return (
    chipNodes(categories, chips).find(node =>
      node.categories?.some(child => child.id === id),
    )?.id ?? null
  );
}

/** `?cat=` → chip id; ids the row would not draw fall back to the first chip. */
export function selectedChip(
  cat: string | null,
  categories: readonly NavigationCategory[],
  chips: readonly Chip[],
): string {
  return cat && allowedChipIds(categories, chips).has(cat)
    ? cat
    : defaultChipId(chips);
}
